import { useState, useEffect, useCallback } from "react";
import { http, resolveImage, API } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Save, Plus, Trash2, Edit, Eye, EyeOff, ChevronDown, X, Download, Check, MessageSquare, FileText } from "lucide-react";

/* ═══ PODCAST SETUP ═══ */
export function AdminEmptyChairSetup() {
  const [s, setS] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [openSection, setOpenSection] = useState(null);

  const load = useCallback(async () => {
    try { const { data } = await http.get("/empty-chair/admin/settings"); setS(data); }
    catch { toast.error("Failed to load"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async (patch) => {
    setSaving(true);
    try { const { data } = await http.patch("/empty-chair/admin/settings", patch); setS(data); toast.success("Saved"); }
    catch { toast.error("Failed"); }
    finally { setSaving(false); }
  };

  if (loading || !s) return <div className="p-10 flex justify-center"><Loader2 className="animate-spin text-[#C9A055]" /></div>;

  const Field = ({ label, field, type = "text", rows }) => (
    <div>
      <label className="text-zinc-400 text-xs uppercase tracking-wider block mb-1.5">{label}</label>
      {rows ? (
        <textarea value={s[field] || ""} onChange={e => setS(p => ({ ...p, [field]: e.target.value }))} rows={rows}
          className="w-full bg-transparent border border-white/10 rounded-lg px-3 py-2 text-sm text-[#F4F1EA] focus:border-[#C9A055] focus:outline-none resize-none" />
      ) : (
        <input type={type} value={s[field] || ""} onChange={e => setS(p => ({ ...p, [field]: e.target.value }))}
          className="w-full bg-transparent border border-white/10 rounded-lg px-3 py-2 text-sm text-[#F4F1EA] focus:border-[#C9A055] focus:outline-none" />
      )}
    </div>
  );

  const ImgField = ({ label, field }) => {
    const upload = async (e) => {
      const file = e.target.files?.[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = async () => {
        try {
          const { data } = await http.post("/upload/image", { data_uri: reader.result, filename: file.name });
          setS(p => ({ ...p, [field]: data.url }));
          toast.success("Uploaded");
        } catch { toast.error("Upload failed"); }
      };
      reader.readAsDataURL(file);
    };
    return (
      <div>
        <label className="text-zinc-400 text-xs uppercase tracking-wider block mb-1.5">{label}</label>
        <div className="flex gap-3 items-center">
          {s[field] && <img src={resolveImage(s[field])} alt="" className="w-16 h-16 rounded-lg object-cover" />}
          <input type="file" accept="image/*" onChange={upload} className="text-xs text-zinc-500" />
          <input value={s[field] || ""} onChange={e => setS(p => ({ ...p, [field]: e.target.value }))} placeholder="or paste URL"
            className="flex-1 bg-transparent border border-white/10 rounded px-2 py-1.5 text-xs text-[#F4F1EA] focus:border-[#C9A055] focus:outline-none" />
        </div>
      </div>
    );
  };

  const sections = [
    { title: "Hero", fields: () => <>
      <Field label="Headline" field="hero_headline" />
      <Field label="Tagline" field="hero_tagline" />
      <Field label="Body Copy" field="hero_copy" rows={4} />
      <div className="grid grid-cols-2 gap-3">
        <Field label="Primary Button" field="hero_primary_btn" />
        <Field label="Secondary Button" field="hero_secondary_btn" />
      </div>
      <Field label="Hosted By" field="hero_hosted_by" />
      <ImgField label="Podcast Artwork" field="hero_artwork" />
      <ImgField label="Background Image" field="hero_bg_image" />
    </> },
    { title: "What Is the Empty Chair?", fields: () => <>
      <Field label="Heading" field="what_heading" />
      <Field label="Body Copy" field="what_copy" rows={6} />
    </> },
    { title: "Why the Chair?", fields: () => <>
      <Field label="Heading" field="why_heading" />
      <Field label="Intro Copy" field="why_copy" />
      <Field label="Closing Line" field="why_closing" />
    </> },
    { title: "Meet the Host", fields: () => <>
      <Field label="Heading" field="host_heading" />
      <ImgField label="Host Photo" field="host_image" />
      <Field label="Bio" field="host_copy" rows={5} />
      <Field label="Quote" field="host_quote" rows={2} />
      <div className="grid grid-cols-2 gap-3">
        <Field label="Button Text" field="host_btn" />
        <Field label="Button Link" field="host_btn_link" />
      </div>
    </> },
    { title: "Newsletter", fields: () => <>
      <Field label="Heading" field="newsletter_heading" />
      <Field label="Copy" field="newsletter_copy" rows={2} />
      <Field label="Button" field="newsletter_btn" />
    </> },
    { title: "Footer CTA", fields: () => <>
      <Field label="Headline" field="footer_headline" />
      <Field label="Copy" field="footer_copy" />
      <Field label="Tagline" field="footer_tagline" rows={2} />
    </> },
    { title: "Contact", fields: () => <>
      <Field label="Heading" field="contact_heading" />
      <Field label="Name" field="contact_name" />
      <Field label="Email" field="contact_email" />
      <Field label="Phone" field="contact_phone" />
      <Field label="Website" field="contact_website" />
    </> },
    { title: "SEO", fields: () => <>
      <Field label="Page Title" field="seo_title" />
      <Field label="Meta Description" field="seo_description" rows={2} />
      <ImgField label="Social Share Image" field="seo_image" />
    </> },
    { title: "Safety Notice", fields: () => <>
      <Field label="Safety Text" field="safety_notice" rows={5} />
    </> },
  ];

  return (
    <div className="space-y-4" data-testid="admin-empty-chair-setup">
      <div className="flex justify-between items-center">
        <h2 className="font-display text-3xl text-[#F4F1EA]">The Empty Chair™ Setup</h2>
        <button onClick={() => save(s)} disabled={saving}
          className="flex items-center gap-2 bg-[#C9A055] text-[#1A1A1A] px-5 py-2 rounded-lg text-xs font-medium hover:bg-[#D4AF37] disabled:opacity-50" data-testid="ec-save-settings">
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save All
        </button>
      </div>
      {sections.map((sec, i) => (
        <div key={i} className="rounded-xl border border-white/[0.06] overflow-hidden">
          <button onClick={() => setOpenSection(openSection === i ? null : i)} className="w-full flex items-center justify-between p-4 text-left hover:bg-white/[0.02] transition-colors">
            <span className="text-[#F4F1EA] text-sm font-medium">{sec.title}</span>
            <ChevronDown size={14} className={`text-zinc-500 transition-transform ${openSection === i ? "rotate-180" : ""}`} />
          </button>
          {openSection === i && <div className="p-4 pt-0 space-y-3 border-t border-white/[0.04]">{sec.fields()}</div>}
        </div>
      ))}
    </div>
  );
}

/* ═══ EPISODES ═══ */
export function AdminEmptyChairEpisodes() {
  const [episodes, setEpisodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    try { const { data } = await http.get("/empty-chair/admin/episodes"); setEpisodes(data); }
    catch { toast.error("Failed"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (loading) return <Loader2 className="animate-spin text-[#C9A055] mx-auto" />;

  if (editing) return <EpisodeEditor ep={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />;

  const create = () => setEditing({ id: null, episode_number: episodes.length + 1, title: "", guest_name: "", description: "", thumbnail: "", youtube_url: "", spotify_url: "", apple_url: "", audio_url: "", transcript_url: "", tags: [], published: false, featured: false, publication_date: "" });
  const del = async (id) => { if (!window.confirm("Delete?")) return; await http.delete(`/empty-chair/admin/episodes/${id}`); load(); };

  return (
    <div className="space-y-4" data-testid="admin-ec-episodes">
      <div className="flex justify-between items-center">
        <h2 className="font-display text-3xl text-[#F4F1EA]">Episodes</h2>
        <button onClick={create} className="flex items-center gap-2 bg-[#C9A055] text-[#1A1A1A] px-4 py-2 rounded-lg text-xs font-medium hover:bg-[#D4AF37]" data-testid="ec-add-episode"><Plus size={14} /> Add Episode</button>
      </div>
      {episodes.length === 0 && <p className="text-zinc-500 text-center py-16">No episodes yet.</p>}
      {episodes.map(ep => (
        <div key={ep.id} className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-4 flex items-center justify-between gap-4" data-testid={`ec-ep-${ep.id}`}>
          <div className="flex items-center gap-4 min-w-0">
            {ep.thumbnail && <img src={resolveImage(ep.thumbnail)} alt="" className="w-16 h-10 rounded object-cover shrink-0" />}
            <div className="min-w-0">
              <div className="flex items-center gap-2 mb-0.5">
                {ep.episode_number > 0 && <span className="text-[10px] text-[#C9A055]">EP {ep.episode_number}</span>}
                {ep.featured && <span className="text-[10px] bg-amber-900/30 text-amber-400 px-1.5 py-0.5 rounded-full">Featured</span>}
                {!ep.published && <span className="text-[10px] text-red-400">Draft</span>}
              </div>
              <p className="text-[#F4F1EA] text-sm truncate">{ep.title || "Untitled"}</p>
              <p className="text-zinc-500 text-xs truncate">{ep.guest_name}</p>
            </div>
          </div>
          <div className="flex gap-1 shrink-0">
            <button onClick={() => setEditing(ep)} className="text-zinc-500 hover:text-[#C9A055] p-2"><Edit size={14} /></button>
            <button onClick={() => del(ep.id)} className="text-zinc-500 hover:text-red-400 p-2"><Trash2 size={14} /></button>
          </div>
        </div>
      ))}
    </div>
  );
}

function EpisodeEditor({ ep, onClose, onSaved }) {
  const [form, setForm] = useState({ ...ep });
  const [saving, setSaving] = useState(false);
  const [transcript, setTranscript] = useState("");
  const [transcriptLoading, setTranscriptLoading] = useState(false);
  const [transcriptSaving, setTranscriptSaving] = useState(false);
  const u = (k, v) => setForm(p => ({ ...p, [k]: v }));

  // Load transcript when editing existing episode
  useEffect(() => {
    if (ep.id) {
      setTranscriptLoading(true);
      http.get(`/empty-chair/admin/transcripts/${ep.id}`)
        .then(r => setTranscript(r.data?.content || ""))
        .catch(() => {})
        .finally(() => setTranscriptLoading(false));
    }
  }, [ep.id]);

  const save = async () => {
    setSaving(true);
    try {
      let epId = form.id;
      if (form.id) {
        await http.patch(`/empty-chair/admin/episodes/${form.id}`, form);
      } else {
        const { data } = await http.post("/empty-chair/admin/episodes", form);
        epId = data.id;
      }
      // Save transcript if provided
      if (transcript.trim() && epId) {
        await http.put(`/empty-chair/admin/transcripts/${epId}`, { content: transcript });
      }
      toast.success("Saved"); onSaved();
    } catch { toast.error("Failed"); }
    finally { setSaving(false); }
  };

  const inp = "w-full bg-transparent border border-white/10 rounded-lg px-3 py-2 text-sm text-[#F4F1EA] focus:border-[#C9A055] focus:outline-none";

  return (
    <div className="space-y-4" data-testid="ec-episode-editor">
      <div className="flex justify-between items-center"><h3 className="text-[#F4F1EA] text-lg">{form.id ? "Edit" : "New"} Episode</h3><button onClick={onClose}><X size={18} className="text-zinc-500" /></button></div>
      <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-5 space-y-3">
        <div className="grid grid-cols-3 gap-3">
          <div><label className="text-zinc-400 text-[10px] uppercase block mb-1">Episode #</label><input type="number" value={form.episode_number || ""} onChange={e => u("episode_number", parseInt(e.target.value) || 0)} className={inp} /></div>
          <div className="col-span-2"><label className="text-zinc-400 text-[10px] uppercase block mb-1">Title *</label><input value={form.title} onChange={e => u("title", e.target.value)} className={inp} data-testid="ec-ep-title" /></div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div><label className="text-zinc-400 text-[10px] uppercase block mb-1">Guest Name</label><input value={form.guest_name} onChange={e => u("guest_name", e.target.value)} className={inp} /></div>
          <div><label className="text-zinc-400 text-[10px] uppercase block mb-1">Publication Date</label><input type="date" value={form.publication_date} onChange={e => u("publication_date", e.target.value)} className={inp} /></div>
        </div>
        <div><label className="text-zinc-400 text-[10px] uppercase block mb-1">Description</label><textarea value={form.description} onChange={e => u("description", e.target.value)} rows={3} className={inp + " resize-none"} /></div>
        <div><label className="text-zinc-400 text-[10px] uppercase block mb-1">Thumbnail URL</label><input value={form.thumbnail} onChange={e => u("thumbnail", e.target.value)} className={inp} /></div>
        <div className="grid grid-cols-2 gap-3">
          <div><label className="text-zinc-400 text-[10px] uppercase block mb-1">YouTube URL</label><input value={form.youtube_url} onChange={e => u("youtube_url", e.target.value)} className={inp} /></div>
          <div><label className="text-zinc-400 text-[10px] uppercase block mb-1">Spotify URL</label><input value={form.spotify_url} onChange={e => u("spotify_url", e.target.value)} className={inp} /></div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div><label className="text-zinc-400 text-[10px] uppercase block mb-1">Apple Podcasts URL</label><input value={form.apple_url} onChange={e => u("apple_url", e.target.value)} className={inp} /></div>
          <div><label className="text-zinc-400 text-[10px] uppercase block mb-1">Transcript URL</label><input value={form.transcript_url} onChange={e => u("transcript_url", e.target.value)} className={inp} /></div>
        </div>
        <div><label className="text-zinc-400 text-[10px] uppercase block mb-1">Tags (comma-separated)</label><input value={(form.tags || []).join(", ")} onChange={e => u("tags", e.target.value.split(",").map(t => t.trim()).filter(Boolean))} className={inp} /></div>
        <div className="flex gap-4">
          <label className="flex items-center gap-2 text-sm text-zinc-300 cursor-pointer"><input type="checkbox" checked={form.published} onChange={e => u("published", e.target.checked)} className="accent-[#C9A055]" /> Published</label>
          <label className="flex items-center gap-2 text-sm text-zinc-300 cursor-pointer"><input type="checkbox" checked={form.featured} onChange={e => u("featured", e.target.checked)} className="accent-[#C9A055]" /> Featured</label>
        </div>
      </div>
      {/* Transcript */}
      <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-5 space-y-3">
        <div className="flex items-center justify-between">
          <p className="text-zinc-400 text-xs uppercase tracking-wider flex items-center gap-2"><FileText size={12} /> Transcript</p>
          {transcriptLoading && <Loader2 size={12} className="animate-spin text-zinc-500" />}
        </div>
        <textarea value={transcript} onChange={e => setTranscript(e.target.value)} rows={8} placeholder="Paste or type the episode transcript here...\n\n[00:00] Mickey: Welcome...\n[00:05] Guest: Thank you..."
          className={inp + " resize-none font-mono text-xs"} data-testid="ec-ep-transcript" />
        <p className="text-zinc-600 text-[10px]">{transcript.length > 0 ? `${transcript.length.toLocaleString()} characters` : "No transcript yet"}</p>
      </div>
      <div className="flex gap-2">
        <button onClick={save} disabled={saving} className="flex items-center gap-2 bg-[#C9A055] text-[#1A1A1A] px-5 py-2 rounded-lg text-xs font-medium disabled:opacity-50" data-testid="ec-ep-save">{saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save</button>
        <button onClick={onClose} className="text-zinc-400 text-xs px-4 py-2">Cancel</button>
      </div>
    </div>
  );
}

/* ═══ GUEST REQUESTS ═══ */
export function AdminEmptyChairRequests() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [viewing, setViewing] = useState(null);

  const load = useCallback(async () => {
    try { const { data } = await http.get(`/empty-chair/admin/requests${filter ? `?status=${filter}` : ""}`); setRequests(data); }
    catch { toast.error("Failed"); }
    finally { setLoading(false); }
  }, [filter]);
  useEffect(() => { load(); }, [load]);

  if (loading) return <Loader2 className="animate-spin text-[#C9A055] mx-auto" />;
  if (viewing) return <RequestDetail req={viewing} onClose={() => { setViewing(null); load(); }} />;

  const STATUSES = ["", "new", "under_review", "follow_up", "potential_guest", "scheduled", "recorded", "published", "not_selected", "archived"];
  const STATUS_LABEL = { "": "All", new: "New", under_review: "Under Review", follow_up: "Follow-Up", potential_guest: "Potential Guest", scheduled: "Scheduled", recorded: "Recorded", published: "Published", not_selected: "Not Selected", archived: "Archived" };
  const STATUS_STYLE = { new: "text-blue-400 bg-blue-900/20", under_review: "text-yellow-400 bg-yellow-900/20", follow_up: "text-orange-400 bg-orange-900/20", potential_guest: "text-purple-400 bg-purple-900/20", scheduled: "text-cyan-400 bg-cyan-900/20", recorded: "text-green-400 bg-green-900/20", published: "text-emerald-400 bg-emerald-900/20", not_selected: "text-zinc-400 bg-zinc-800", archived: "text-zinc-500 bg-zinc-800" };

  return (
    <div className="space-y-4" data-testid="admin-ec-requests">
      <h2 className="font-display text-3xl text-[#F4F1EA]">Guest Requests</h2>
      <div className="flex flex-wrap gap-1.5">
        {STATUSES.map(st => (
          <button key={st} onClick={() => setFilter(st)} className={`px-3 py-1.5 rounded-full text-[10px] font-medium tracking-wide transition-all ${filter === st ? "bg-[#C9A055] text-[#1A1A1A]" : "border border-white/10 text-zinc-500 hover:border-[#C9A055]/30"}`}>{STATUS_LABEL[st]}</button>
        ))}
      </div>
      <p className="text-zinc-400 text-sm">{requests.length} request{requests.length !== 1 ? "s" : ""}</p>
      {requests.map(r => (
        <button key={r.id} onClick={() => setViewing(r)} className="w-full rounded-lg border border-white/[0.06] bg-white/[0.02] p-4 flex items-center justify-between gap-4 hover:border-[#C9A055]/30 transition-all text-left" data-testid={`ec-req-${r.id}`}>
          <div className="min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <span className={`text-[10px] font-medium uppercase tracking-wider px-2 py-0.5 rounded-full ${STATUS_STYLE[r.status] || "bg-zinc-800 text-zinc-400"}`}>{r.status?.replace(/_/g, " ")}</span>
              {r.priority && <span className="text-[10px] text-amber-400">Priority</span>}
              {r.legal_review && <span className="text-[10px] text-red-400">Legal Review</span>}
              {r.crisis_flag && <span className="text-[10px] text-red-500 font-bold">CRISIS FLAG</span>}
            </div>
            <p className="text-[#F4F1EA] text-sm font-medium truncate">{r.full_name}</p>
            <p className="text-zinc-500 text-xs">{r.email} · {r.city}{r.state ? `, ${r.state}` : ""}</p>
          </div>
          <span className="text-zinc-600 text-xs shrink-0">{new Date(r.created_at).toLocaleDateString()}</span>
        </button>
      ))}
    </div>
  );
}

function RequestDetail({ req, onClose }) {
  const [r, setR] = useState(req);
  const [saving, setSaving] = useState(false);

  const patch = async (upd) => {
    setSaving(true);
    try { const { data } = await http.patch(`/empty-chair/admin/requests/${r.id}`, upd); setR(data); toast.success("Updated"); }
    catch { toast.error("Failed"); }
    finally { setSaving(false); }
  };

  const STATUSES = ["new","under_review","follow_up","potential_guest","scheduled","recorded","published","not_selected","archived"];
  const Row = ({ label, val }) => val ? <div className="border-b border-white/[0.04] pb-2"><p className="text-zinc-500 text-[10px] uppercase mb-0.5">{label}</p><p className="text-zinc-300 text-sm whitespace-pre-wrap">{Array.isArray(val) ? val.join(", ") : val}</p></div> : null;

  return (
    <div className="space-y-4" data-testid="ec-req-detail">
      <div className="flex justify-between items-center"><h3 className="text-[#F4F1EA] text-lg">{r.full_name}</h3><button onClick={onClose}><X size={18} className="text-zinc-500" /></button></div>
      <div className="flex gap-2 items-center flex-wrap">
        <select value={r.status} onChange={e => patch({ status: e.target.value })} className="bg-[#1A1A1A] border border-white/10 rounded-lg px-3 py-2 text-sm text-[#F4F1EA] focus:border-[#C9A055] focus:outline-none">
          {STATUSES.map(s => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
        </select>
        <label className="flex items-center gap-2 text-xs text-zinc-300 cursor-pointer"><input type="checkbox" checked={r.priority} onChange={e => patch({ priority: e.target.checked })} className="accent-[#C9A055]" /> Priority</label>
        <label className="flex items-center gap-2 text-xs text-zinc-300 cursor-pointer"><input type="checkbox" checked={r.legal_review} onChange={e => patch({ legal_review: e.target.checked })} className="accent-red-400" /> Legal Review</label>
      </div>
      <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-5 space-y-3">
        <p className="text-[#C9A055] text-xs uppercase tracking-wider">Contact</p>
        <Row label="Name" val={r.full_name} /><Row label="Preferred Name" val={r.preferred_name} />
        <Row label="Email" val={r.email} /><Row label="Phone" val={r.phone} />
        <Row label="Location" val={[r.city, r.state, r.country].filter(Boolean).join(", ")} />
        <Row label="Pronouns" val={r.pronouns} /><Row label="Website" val={r.website} /><Row label="Social" val={r.social_links} />
      </div>
      <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-5 space-y-3">
        <p className="text-[#C9A055] text-xs uppercase tracking-wider">Story</p>
        <Row label="Story" val={r.story_share} /><Row label="Why it Matters" val={r.why_matters} />
        <Row label="Listener Takeaway" val={r.listener_takeaway} /><Row label="What Changed" val={r.what_changed} />
        <Row label="Shared Before?" val={r.shared_publicly} /><Row label="Off Limits" val={r.off_limits} />
        <Row label="Special Care" val={r.special_care} /><Row label="Allegations" val={r.allegations} /><Row label="Allegations Detail" val={r.allegations_detail} />
      </div>
      <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-5 space-y-3">
        <p className="text-[#C9A055] text-xs uppercase tracking-wider">Preferences</p>
        <Row label="Topics" val={r.topics} /><Row label="Recording Pref" val={r.recording_pref} />
        <Row label="Privacy" val={r.privacy_prefs} /><Row label="Availability" val={[r.availability_days, r.availability_times, r.timezone].filter(Boolean).join(" · ")} />
        <Row label="Interviewed Before" val={r.interviewed_before} /><Row label="Media Sample" val={r.media_sample} />
      </div>
      {r.crisis_flag && <div className="rounded-xl border border-red-500/30 bg-red-500/10 p-4"><p className="text-red-400 text-sm font-bold">CRISIS FLAG — This person indicated their story is connected to a current crisis or emergency.</p></div>}
      <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-5 space-y-3">
        <p className="text-[#C9A055] text-xs uppercase tracking-wider">Admin</p>
        <div><label className="text-zinc-400 text-[10px] uppercase block mb-1">Notes</label>
          <textarea value={r.admin_notes || ""} onChange={e => setR(p => ({ ...p, admin_notes: e.target.value }))} rows={3}
            className="w-full bg-transparent border border-white/10 rounded-lg px-3 py-2 text-sm text-[#F4F1EA] focus:border-[#C9A055] focus:outline-none resize-none" />
          <button onClick={() => patch({ admin_notes: r.admin_notes })} className="mt-1 text-[#C9A055] text-xs hover:underline">Save notes</button>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div><label className="text-zinc-400 text-[10px] uppercase block mb-1">Episode #</label><input value={r.episode_number || ""} onChange={e => setR(p => ({ ...p, episode_number: e.target.value }))} onBlur={() => patch({ episode_number: r.episode_number })} className="w-full bg-transparent border border-white/10 rounded px-2 py-1.5 text-sm text-[#F4F1EA] focus:border-[#C9A055] focus:outline-none" /></div>
          <div><label className="text-zinc-400 text-[10px] uppercase block mb-1">Recording Date</label><input type="date" value={r.recording_date || ""} onChange={e => patch({ recording_date: e.target.value })} className="w-full bg-[#1A1A1A] border border-white/10 rounded px-2 py-1.5 text-sm text-[#F4F1EA] focus:border-[#C9A055] focus:outline-none" /></div>
        </div>
      </div>
      <p className="text-zinc-600 text-[10px]">Submitted {new Date(r.created_at).toLocaleString()} · IP: {r.signature_ip}</p>
      <button onClick={onClose} className="text-zinc-400 text-xs hover:text-zinc-200">Back to list</button>
    </div>
  );
}

/* ═══ FAQ ═══ */
export function AdminEmptyChairFAQ() {
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    try { const { data } = await http.get("/empty-chair/admin/faq"); setFaqs(data); }
    catch { toast.error("Failed"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const seed = async () => { await http.post("/empty-chair/admin/seed-faq"); load(); };
  const del = async (id) => { if (!window.confirm("Delete?")) return; await http.delete(`/empty-chair/admin/faq/${id}`); load(); };
  const save = async () => {
    if (!editing) return;
    try {
      if (editing.id) await http.patch(`/empty-chair/admin/faq/${editing.id}`, editing);
      else await http.post("/empty-chair/admin/faq", editing);
      toast.success("Saved"); setEditing(null); load();
    } catch { toast.error("Failed"); }
  };

  if (loading) return <Loader2 className="animate-spin text-[#C9A055] mx-auto" />;

  return (
    <div className="space-y-4" data-testid="admin-ec-faq">
      <div className="flex justify-between items-center">
        <h2 className="font-display text-3xl text-[#F4F1EA]">Podcast FAQ</h2>
        <div className="flex gap-2">
          {faqs.length === 0 && <button onClick={seed} className="text-[#C9A055] text-xs hover:underline">Seed Defaults</button>}
          <button onClick={() => setEditing({ id: null, question: "", answer: "", sort_order: faqs.length, visible: true })} className="flex items-center gap-2 bg-[#C9A055] text-[#1A1A1A] px-4 py-2 rounded-lg text-xs font-medium hover:bg-[#D4AF37]"><Plus size={14} /> Add</button>
        </div>
      </div>
      {editing && (
        <div className="rounded-xl border border-[#C9A055]/20 bg-[#C9A055]/[0.03] p-5 space-y-3">
          <input value={editing.question} onChange={e => setEditing(p => ({ ...p, question: e.target.value }))} placeholder="Question"
            className="w-full bg-transparent border border-white/10 rounded-lg px-3 py-2 text-sm text-[#F4F1EA] focus:border-[#C9A055] focus:outline-none" />
          <textarea value={editing.answer} onChange={e => setEditing(p => ({ ...p, answer: e.target.value }))} placeholder="Answer" rows={3}
            className="w-full bg-transparent border border-white/10 rounded-lg px-3 py-2 text-sm text-[#F4F1EA] focus:border-[#C9A055] focus:outline-none resize-none" />
          <div className="flex gap-2">
            <button onClick={save} className="bg-[#C9A055] text-[#1A1A1A] px-4 py-2 rounded-lg text-xs font-medium"><Save size={14} className="inline mr-1" />Save</button>
            <button onClick={() => setEditing(null)} className="text-zinc-400 text-xs">Cancel</button>
          </div>
        </div>
      )}
      {faqs.map(f => (
        <div key={f.id} className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-4 flex items-center justify-between gap-4">
          <div className="min-w-0"><p className="text-[#F4F1EA] text-sm truncate">{f.question}</p><p className="text-zinc-500 text-xs truncate">{f.answer.slice(0, 80)}...</p></div>
          <div className="flex gap-1 shrink-0">
            <button onClick={() => setEditing(f)} className="text-zinc-500 hover:text-[#C9A055] p-1"><Edit size={14} /></button>
            <button onClick={() => del(f.id)} className="text-zinc-500 hover:text-red-400 p-1"><Trash2 size={14} /></button>
          </div>
        </div>
      ))}
    </div>
  );
}


/* ═══ TESTIMONIALS ADMIN ═══ */
export function AdminEmptyChairTestimonials() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");

  const load = useCallback(async () => {
    try { const { data } = await http.get(`/empty-chair/admin/testimonials${filter ? `?approved=${filter}` : ""}`); setItems(data); }
    catch { toast.error("Failed"); }
    finally { setLoading(false); }
  }, [filter]);
  useEffect(() => { load(); }, [load]);

  const toggle = async (id, approved) => {
    await http.patch(`/empty-chair/admin/testimonials/${id}`, { approved: !approved });
    load();
  };
  const del = async (id) => { if (!window.confirm("Delete?")) return; await http.delete(`/empty-chair/admin/testimonials/${id}`); load(); };

  if (loading) return <Loader2 className="animate-spin text-[#C9A055] mx-auto" />;

  return (
    <div className="space-y-4" data-testid="admin-ec-testimonials">
      <h2 className="font-display text-3xl text-[#F4F1EA]">Testimonials & Responses</h2>
      <div className="flex gap-2">
        {[["", "All"], ["false", "Pending"], ["true", "Approved"]].map(([v, l]) => (
          <button key={v} onClick={() => setFilter(v)} className={`px-3 py-1.5 rounded-full text-[10px] font-medium transition-all ${filter === v ? "bg-[#C9A055] text-[#1A1A1A]" : "border border-white/10 text-zinc-500"}`}>{l}</button>
        ))}
      </div>
      <p className="text-zinc-400 text-sm">{items.length} item{items.length !== 1 ? "s" : ""}</p>
      {items.map(t => (
        <div key={t.id} className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-4 space-y-2" data-testid={`testimonial-${t.id}`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className={`text-[10px] px-2 py-0.5 rounded-full ${t.approved ? "bg-green-900/20 text-green-400" : "bg-yellow-900/20 text-yellow-400"}`}>{t.approved ? "Approved" : "Pending"}</span>
              <span className="text-zinc-500 text-[10px]">{t.type}</span>
            </div>
            <span className="text-zinc-600 text-xs">{new Date(t.created_at).toLocaleDateString()}</span>
          </div>
          <p className="text-zinc-300 text-sm italic">"{t.text}"</p>
          <p className="text-zinc-500 text-xs">{t.name} · {t.email}{t.episode_title ? ` · on "${t.episode_title}"` : ""}</p>
          <div className="flex gap-2 pt-1">
            <button onClick={() => toggle(t.id, t.approved)} className="text-xs text-[#C9A055] hover:underline">{t.approved ? "Unapprove" : "Approve"}</button>
            <button onClick={() => del(t.id)} className="text-xs text-red-400 hover:underline">Delete</button>
          </div>
        </div>
      ))}
    </div>
  );
}

/* ═══ MEDIA INQUIRIES ADMIN ═══ */
export function AdminEmptyChairInquiries() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewing, setViewing] = useState(null);

  const load = useCallback(async () => {
    try { const { data } = await http.get("/empty-chair/admin/inquiries"); setItems(data); }
    catch { toast.error("Failed"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const del = async (id) => { if (!window.confirm("Delete?")) return; await http.delete(`/empty-chair/admin/inquiries/${id}`); load(); };
  const patch = async (id, upd) => { await http.patch(`/empty-chair/admin/inquiries/${id}`, upd); load(); };

  if (loading) return <Loader2 className="animate-spin text-[#C9A055] mx-auto" />;

  if (viewing) {
    const i = viewing;
    return (
      <div className="space-y-4" data-testid="ec-inquiry-detail">
        <div className="flex justify-between"><h3 className="text-[#F4F1EA] text-lg">{i.name}</h3><button onClick={() => { setViewing(null); load(); }}><X size={18} className="text-zinc-500" /></button></div>
        <div className="flex items-center gap-2">
          <select value={i.status} onChange={e => { patch(i.id, { status: e.target.value }); setViewing({ ...i, status: e.target.value }); }}
            className="bg-[#1A1A1A] border border-white/10 rounded-lg px-3 py-2 text-sm text-[#F4F1EA]">
            {["new","in_progress","responded","closed"].map(s => <option key={s} value={s}>{s.replace(/_/g," ")}</option>)}
          </select>
        </div>
        <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-5 space-y-2">
          <p className="text-zinc-500 text-xs">Organization: <span className="text-zinc-300">{i.organization || "—"}</span></p>
          <p className="text-zinc-500 text-xs">Email: <span className="text-zinc-300">{i.email}</span></p>
          <p className="text-zinc-500 text-xs">Phone: <span className="text-zinc-300">{i.phone || "—"}</span></p>
          <p className="text-zinc-500 text-xs">Type: <span className="text-[#C9A055]">{i.inquiry_type}</span></p>
          <p className="text-zinc-300 text-sm mt-3 whitespace-pre-wrap">{i.message}</p>
        </div>
        <p className="text-zinc-600 text-[10px]">Received {new Date(i.created_at).toLocaleString()}</p>
      </div>
    );
  }

  const STATUS_STYLE = { new: "text-blue-400 bg-blue-900/20", in_progress: "text-yellow-400 bg-yellow-900/20", responded: "text-green-400 bg-green-900/20", closed: "text-zinc-400 bg-zinc-800" };

  return (
    <div className="space-y-4" data-testid="admin-ec-inquiries">
      <div className="flex justify-between items-center">
        <h2 className="font-display text-3xl text-[#F4F1EA]">Media Inquiries</h2>
        <a href={`${API}/empty-chair/admin/export/inquiries`} target="_blank" rel="noreferrer"
          className="flex items-center gap-2 border border-[#C9A055] text-[#C9A055] px-4 py-2 rounded-lg text-xs font-medium hover:bg-[#C9A055] hover:text-[#1A1A1A] transition-all" data-testid="ec-export-inquiries">
          <Download size={14} /> Export CSV
        </a>
      </div>
      <p className="text-zinc-400 text-sm">{items.length} inquir{items.length !== 1 ? "ies" : "y"}</p>
      {items.map(i => (
        <button key={i.id} onClick={() => setViewing(i)} className="w-full rounded-lg border border-white/[0.06] bg-white/[0.02] p-4 flex items-center justify-between gap-4 hover:border-[#C9A055]/30 transition-all text-left">
          <div className="min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <span className={`text-[10px] px-2 py-0.5 rounded-full ${STATUS_STYLE[i.status] || "bg-zinc-800 text-zinc-400"}`}>{i.status?.replace(/_/g," ")}</span>
              <span className="text-[10px] text-[#C9A055]">{i.inquiry_type}</span>
            </div>
            <p className="text-[#F4F1EA] text-sm truncate">{i.name}{i.organization ? ` — ${i.organization}` : ""}</p>
            <p className="text-zinc-500 text-xs truncate">{i.email}</p>
          </div>
          <span className="text-zinc-600 text-xs shrink-0">{new Date(i.created_at).toLocaleDateString()}</span>
        </button>
      ))}
    </div>
  );
}

/* ═══ EXPORT & TOOLS ═══ */
export function AdminEmptyChairExport() {
  const token = localStorage.getItem("stc_token");
  const headers = token ? `?token=${token}` : "";

  return (
    <div className="space-y-6" data-testid="admin-ec-export">
      <h2 className="font-display text-3xl text-[#F4F1EA]">Export & Tools</h2>
      <div className="grid sm:grid-cols-2 gap-4">
        <a href={`${API}/empty-chair/admin/export/guest-requests`} target="_blank" rel="noreferrer"
          className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-6 hover:border-[#C9A055]/30 transition-all block" data-testid="ec-export-requests">
          <Download size={20} className="text-[#C9A055] mb-3" />
          <h3 className="text-[#F4F1EA] text-sm font-medium mb-1">Guest Requests CSV</h3>
          <p className="text-zinc-500 text-xs">Download all guest request submissions as a spreadsheet.</p>
        </a>
        <a href={`${API}/empty-chair/admin/export/inquiries`} target="_blank" rel="noreferrer"
          className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-6 hover:border-[#C9A055]/30 transition-all block" data-testid="ec-export-inquiries-card">
          <Download size={20} className="text-[#C9A055] mb-3" />
          <h3 className="text-[#F4F1EA] text-sm font-medium mb-1">Media Inquiries CSV</h3>
          <p className="text-zinc-500 text-xs">Download all media and collaboration inquiries.</p>
        </a>
      </div>
    </div>
  );
}
